'use client';

/**
 * RejectionBanner Component
 *
 * Displays document rejection reasons with a clear visual indicator
 * and optional re-upload call-to-action.
 *
 * Issue #43: Display document rejection reasons to borrowers
 */

import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import Upload from '@mui/icons-material/Upload';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { GlassButton } from '../GlassButton/GlassButton';
import { GlassCard } from '../GlassCard/GlassCard';

export interface RejectionBannerProps {
  /**
   * The rejection/re-upload reason to display
   */
  reason: string;

  /**
   * Banner variant: 'rejected' for failed verification, 'needs_reupload' for re-upload request
   * @default 'rejected'
   */
  variant?: 'rejected' | 'needs_reupload';

  /**
   * Callback when re-upload button is clicked
   */
  onReupload?: () => void;

  /**
   * Compact mode for dashboard display
   * @default false
   */
  compact?: boolean;

  /**
   * Additional styles
   */
  sx?: SxProps<Theme>;
}

export function RejectionBanner({
  reason,
  variant = 'rejected',
  onReupload,
  compact = false,
  sx,
}: RejectionBannerProps) {
  const { t } = useI18n();

  const isReuploadRequest = variant === 'needs_reupload';
  const accentColor = isReuploadRequest ? 'warning.main' : 'error.main';
  const neonAccent = isReuploadRequest ? 'warning' : 'error';
  const reasonKey = isReuploadRequest ? 'documents.reuploadRequest.reason' : 'documents.rejection.reason';
  const helperKey = isReuploadRequest
    ? 'documents.reuploadRequest.uploadNewVersion'
    : 'documents.rejection.uploadCorrected';
  const buttonKey = isReuploadRequest ? 'documents.reuploadRequest.reupload' : 'documents.rejection.reupload';

  return (
    <GlassCard
      variant="outlined"
      neonAccent={neonAccent}
      glowOnHover
      sx={{
        mt: compact ? 1 : 2,
        p: compact ? 1.5 : 2,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <ErrorOutlined
          sx={{
            color: accentColor,
            fontSize: compact ? 18 : 20,
            mt: 0.25,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant={compact ? 'caption' : 'body2'}
            sx={{
              color: accentColor,
              fontWeight: 500,
            }}
          >
            {t(reasonKey, { reason })}
          </Typography>
          {!compact && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              {t(helperKey)}
            </Typography>
          )}
        </Box>
      </Box>

      {onReupload && (
        <Box sx={{ mt: compact ? 1 : 1.5 }}>
          <GlassButton
            variant="outlined"
            size="small"
            startIcon={<Upload />}
            onClick={onReupload}
            sx={{
              borderColor: accentColor,
              color: accentColor,
            }}
          >
            {t(buttonKey)}
          </GlassButton>
        </Box>
      )}
    </GlassCard>
  );
}

export default RejectionBanner;
